import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const emptyForm = { name: "", code: "", documentType: "SALES_INVOICE", brandId: "", active: true };

const documentTypeLabels = {
  SALES_ORDER: "Sales Order",
  SALES_INVOICE: "Sales Invoice",
  CREDIT_NOTE: "Credit Note",
  DEBIT_NOTE: "Debit Note",
};

const VoucherTypes = () => {
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [voucherData, brandsData] = await Promise.all([
        api.get("/api/v1/voucher-types", { size: 200 }),
        api.get("/api/v1/brands", { size: 200 }),
      ]);
      setVoucherTypes(voucherData?.content || []);
      setBrands(brandsData?.content || []);
    } catch (error) {
      toast.error(error.message || "Failed to load voucher types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (voucherType) => {
    setEditingId(voucherType.id);
    setFormData({
      name: voucherType.name || "",
      code: voucherType.code || "",
      documentType: voucherType.documentType || "SALES_INVOICE",
      brandId: voucherType.brandId ?? "",
      active: voucherType.active ?? true,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, brandId: formData.brandId ? Number(formData.brandId) : null };
      if (editingId) {
        await api.put(`/api/v1/voucher-types/${editingId}`, payload);
        toast.success("Voucher type updated");
      } else {
        await api.post("/api/v1/voucher-types", payload);
        toast.success("Voucher type created");
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || "Failed to save voucher type");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading voucher types...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Voucher types are named number series used when manually creating a document — e.g. "ITC Sales" or "ITC
        Credit Note" (Brand + Category naming). Each gets its own prefix and running number, separate from the
        default numbering.
      </div>

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Voucher Type
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Code (Prefix)</th>
              <th>Document Type</th>
              <th>Brand</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {voucherTypes.length === 0 ? (
              <tr>
                <td colSpan={6}>No voucher types found.</td>
              </tr>
            ) : (
              voucherTypes.map((vt) => (
                <tr key={vt.id}>
                  <td>{vt.name}</td>
                  <td>{vt.code}</td>
                  <td>{documentTypeLabels[vt.documentType] || vt.documentType}</td>
                  <td>{vt.brandName || "-"}</td>
                  <td>
                    <span className={`badge ${vt.active ? "badge-green" : "badge-gray"}`}>{vt.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="action-buttons">
                    <button className="secondary" onClick={() => openEdit(vt)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Voucher Type" : "Create Voucher Type"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Name</label>
                  <input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. ITC Sales" />
                </div>
                <div className="form-group">
                  <label>Code / Prefix (max 10 chars)</label>
                  <input name="code" value={formData.code} onChange={handleChange} required maxLength={10} placeholder="e.g. ITCS" />
                </div>
                <div className="form-group">
                  <label>Document Type</label>
                  <select name="documentType" value={formData.documentType} onChange={handleChange}>
                    {Object.entries(documentTypeLabels).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Brand (optional)</label>
                  <select name="brandId" value={formData.brandId} onChange={handleChange}>
                    <option value="">No brand</option>
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Active</label>
                  <select
                    name="active"
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default VoucherTypes;
